import { create } from "zustand";
import { useContext, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Editor } from "@tiptap/react";

import { LinkPanel } from "../LinkPanel/LinkPanel";
import { linkPanelContext } from "../LinkPanel/context";
import { Button } from "@/components/ui/button";
import { useCopyText } from "@/hooks/navigator";

type CustomLinkStore = {
  isLinkPanelVisible: boolean;
  linkPos: DOMRect | null;
  href: string | null;
  setHref: (href: string | null) => void;
  setLinkPos: (rect: DOMRect | null) => void;
  setIsLinkPanelVisible: (isVisible: boolean) => void;
};

export const useCustomLinkStore = create<CustomLinkStore>((set) => ({
  isLinkPanelVisible: false,
  linkPos: null,
  href: null,
  setHref: (href) => set({ href }),
  setLinkPos: (linkPos) => set({ linkPos }),
  setIsLinkPanelVisible: (isLinkPanelVisible) => set({ isLinkPanelVisible }),
}));

export const LinkUrlTooltip = ({ editor }: { editor: Editor }) => {
  const { refs, style } = useContext(linkPanelContext);
  const {
    href,
    linkPos,
    isLinkPanelVisible,
    setIsLinkPanelVisible,
    setHref,
    setLinkPos,
  } = useCustomLinkStore();

  const [, copyUrl] = useCopyText();

  useEffect(() => {
    const editorArea = document.getElementById("editor-area") as HTMLDivElement;

    const onMouseOver = (e: MouseEvent) => {
      const link: HTMLElement | null = (e.target as HTMLElement).closest("a");
      let editorElem: HTMLElement | null = link;

      while (
        editorElem != null &&
        editorElem.parentElement !== null &&
        Boolean(editorElem?.getAttribute("data-editor")) === false
      ) {
        editorElem = editorElem?.parentElement;
      }

      // We don't want to open the Tooltip for any other links. Just to be sure.
      const isLinkInEditor = !!editorElem;
      const isNotEmbed =
        link?.attributes.getNamedItem("data-embed-preview") === null;

      if (!!isLinkInEditor && !!link && isNotEmbed) {
        const href = link.getAttribute("href")!;
        const linkRect = link.getBoundingClientRect();

        setHref(href);
        setLinkPos(linkRect);
      }

      if (
        !refs!.floating.current?.contains(e.target as HTMLDivElement) &&
        e.target !== link
      ) {
        setLinkPos(null);
      }
    };

    editorArea.addEventListener("mouseover", onMouseOver);

    return () => editorArea.removeEventListener("mouseover", onMouseOver);
  }, [href, refs, setHref, setLinkPos]);

  useLayoutEffect(() => {
    if (linkPos !== null) {
      refs?.setPositionReference({ getBoundingClientRect: () => linkPos });
    }
  }, [refs?.setPositionReference, linkPos]);

  const onToolbarOpen = () => {
    const position = editor.view.posAtCoords({
      left: linkPos!.left,
      top: linkPos!.top,
    });

    if (!!position) {
      const elem = editor.view.domAtPos(position.pos + 1);

      editor
        .chain()
        .setTextSelection({
          from: position.pos,
          to: position.pos + (elem?.node as any).length,
        })
        .run();
    }

    setLinkPos(null);
    setIsLinkPanelVisible(true);
  };

  return (
    <>
      {linkPos !== null && !isLinkPanelVisible
        ? createPortal(
            <div
              ref={refs?.setFloating}
              style={{ ...style }}
              className="relative z-20 overflow-hidden rounded py-0.5 px-2 shadow-lg bg-black flex items-center gap-1"
            >
              <div className="text-sm text-gray-100 font-semibold">{href}</div>
              <Button
                type="button"
                variant="link"
                size="xs"
                onClick={onToolbarOpen}
              >
                Edit
              </Button>
              {href !== null && (
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  onClick={() => {
                    copyUrl(href);
                    setHref(null);
                    setLinkPos(null);
                  }}
                >
                  Copy
                </Button>
              )}
            </div>,
            document.getElementById("editor-area") as HTMLDivElement
          )
        : null}
      {isLinkPanelVisible &&
        createPortal(
          <LinkPanel
            ref={refs?.setFloating}
            editor={editor}
            onLinkSet={() => {
              setIsLinkPanelVisible(false);
              setHref(null);
            }}
            href={href}
          />,
          document.getElementById("editor-area") as HTMLDivElement
        )}
    </>
  );
};
